import React, { useEffect, useState } from "react";
import api, { formatTRY } from "@/lib/api";
import { XCircle, LoaderCircle, RotateCcw, Pause } from "lucide-react";

const ST_LABEL = {
    cancelled: "İptal", refunded: "İade Edildi", refund_pending: "İade Bekliyor", rights_reserved: "Hakkı Saklı",
};
const ST_COLOR = {
    cancelled: "bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300",
    refunded: "bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300",
    refund_pending: "bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300",
    rights_reserved: "bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300",
};

export default function CancellationsTab({ dateFrom, dateTo }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/reports/cancellations?date_from=${dateFrom}&date_to=${dateTo}`)
            .then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    if (loading) return <div className="p-8 text-center"><LoaderCircle className="h-6 w-6 mx-auto animate-spin text-violet-500" /></div>;
    if (!data) return null;

    return (
        <div data-testid="tab-cancellations">
            {/* Özet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Stat icon={XCircle} label="Toplam Kayıt" value={data.totals.count} color="slate" />
                <Stat icon={XCircle} label="Kayıp Ciro" value={formatTRY(data.totals.lost_revenue)} color="rose" />
                <Stat icon={RotateCcw} label="İade Edilen" value={formatTRY(data.totals.refunded_amount)} color="sky" />
                <Stat icon={Pause} label="Hakkı Saklı (Bakiye)" value={formatTRY(data.totals.credit_held)} color="violet" />
            </div>

            {/* Status kırılımı */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="text-sm font-bold mb-3">Durum Dağılımı</h3>
                    <div className="space-y-2">
                        {Object.entries(data.by_status).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-sm">
                                <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${ST_COLOR[k]}`}>{ST_LABEL[k] || k}</span>
                                <div className="text-right">
                                    <span className="font-bold">{v.count}</span>
                                    <span className="text-xs text-slate-500 ml-2">({formatTRY(v.amount)})</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Otele göre iptal */}
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="text-sm font-bold mb-3">En Çok İptal Olan Oteller</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {data.by_hotel.length === 0 ? (
                            <div className="text-xs text-slate-500 py-4 text-center">Kayıt yok</div>
                        ) : data.by_hotel.map((h, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                                <span className="truncate">{h.hotel}</span>
                                <div className="text-right shrink-0">
                                    <span className="font-bold text-rose-600">{h.count}</span>
                                    <span className="text-xs text-slate-500 ml-2">{formatTRY(h.amount)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Detay listesi */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 text-sm font-bold">Son İptal / İade / Hakkı Saklı Kayıtları</div>
                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900/95 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">Voucher</th>
                                <th className="text-left px-3 py-2">Otel</th>
                                <th className="text-left px-3 py-2">Misafir</th>
                                <th className="text-right px-3 py-2">Tutar</th>
                                <th className="text-right px-3 py-2">İade</th>
                                <th className="text-left px-3 py-2">Durum</th>
                                <th className="text-left px-3 py-2">Neden</th>
                                <th className="text-left px-3 py-2">Tarih</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.details.length === 0 ? (
                                <tr><td colSpan={8} className="py-12 text-center text-slate-500">Bu dönemde iptal/iade yok</td></tr>
                            ) : data.details.map((r) => (
                                <tr key={r.booking_id} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`cancel-row-${r.booking_id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px]">{r.voucher_no}</td>
                                    <td className="px-3 py-2 text-xs">{r.hotel_name}</td>
                                    <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                                    <td className="px-3 py-2 text-right text-xs">{formatTRY(r.total_amount)}</td>
                                    <td className="px-3 py-2 text-right text-xs text-orange-600">{r.refund_amount ? formatTRY(r.refund_amount) : "—"}</td>
                                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${ST_COLOR[r.status]}`}>{ST_LABEL[r.status]}</span></td>
                                    <td className="px-3 py-2 text-[11px] text-slate-600 dark:text-slate-400 max-w-[200px] truncate" title={r.reason}>{r.reason || "—"}</td>
                                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.date}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, color }) {
    const clr = { slate: "text-slate-600 dark:text-slate-300", rose: "text-rose-600 dark:text-rose-400", sky: "text-sky-600 dark:text-sky-400", violet: "text-violet-600 dark:text-violet-400" }[color];
    return (
        <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className={`flex items-center gap-2 ${clr}`}>
                <Icon className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
            </div>
            <div className="mt-1 text-lg font-black">{value}</div>
        </div>
    );
}
